import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StaffListView, StaffMemberView, StaffUploadPreview } from '@nest-recruit/shared';
import { ApiError, apiGet, apiPost, request } from '../../../lib/api-client';

// ADR 0074 — the staff list (Admin → Staff list) and a manager's own team (Request a hire).

export function useStaffList() {
  return useQuery<StaffListView, ApiError>({
    queryKey: ['staff', 'list'],
    queryFn: () => apiGet<StaffListView>('/admin/staff'),
  });
}

/** "Who is being replaced?": the viewer's own team, read once and kept while the form is open. */
export function useMyTeam(isWanted = true) {
  return useQuery<readonly StaffMemberView[], ApiError>({
    queryKey: ['staff', 'my-team'],
    queryFn: async () => (await apiGet<{ members: readonly StaffMemberView[] }>('/staff/my-team')).members,
    enabled: isWanted,
    staleTime: 5 * 60_000,
  });
}

/**
 * QA D50: "Who is being replaced?" on a request someone else raised (HR editing a manager's draft) lists the requester's team,
 * not the viewer's; a new request, or one's own, the viewer's team.
 */
export function useTeamToReplace(requisitionId: string | null, isWanted = true) {
  const own = useMyTeam(isWanted && requisitionId === null);
  const theirs = useQuery<readonly StaffMemberView[], ApiError>({
    queryKey: ['staff', 'replaceable', requisitionId],
    queryFn: async () => (await apiGet<{ members: readonly StaffMemberView[] }>(`/requisitions/${requisitionId}/replaceable`)).members,
    enabled: isWanted && requisitionId !== null,
    staleTime: 5 * 60_000,
  });
  return requisitionId === null ? own : theirs;
}

export function usePreviewStaffUpload() {
  return useMutation<StaffUploadPreview, ApiError, File>({
    mutationFn: async (file) => {
      const body = new FormData();
      body.append('file', file);
      return (await request<StaffUploadPreview>('/admin/staff/uploads', { method: 'POST', body })).data;
    },
  });
}

export function useApplyStaffUpload() {
  const client = useQueryClient();
  return useMutation<StaffListView, ApiError, string>({
    mutationFn: (uploadId) => apiPost<StaffListView>(`/admin/staff/uploads/${uploadId}/apply`),
    onSuccess: (list) => {
      client.setQueryData(['staff', 'list'], list);
      void client.invalidateQueries({ queryKey: ['staff', 'my-team'] });
    },
  });
}
